import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { FolderTreeArt } from "../components/adminSurface/heroArt";
import {
  getDatabases,
  getStorageLocations,
  getRecordCategories,
  createRecordCategory,
  updateRecordCategory,
  moveRecordCategory,
  archiveRecordCategory,
  restoreRecordCategory,
  setRecordSchedule,
  getRecordSeriesRules,
  createRecordSeriesRule,
  updateRecordSeriesRule,
  deleteRecordSeriesRule,
  testRecordSeriesRule,
  getDocumentRecord,
  declareDocument,
  setDocumentCutoff,
  triggerRecordsBackfill,
  getRecordsBackfillStatus,
  cancelRecordsBackfill,
} from "../api/adminApi";

const RM_STYLES = `
.rm-hero{ position:relative; overflow:hidden; border-radius:18px; padding:24px 28px; margin-bottom:20px;
  background:linear-gradient(135deg,#0f766e 0%,#0d9488 46%,#0891b2 100%);
  box-shadow:0 14px 38px -10px rgba(13,148,136,.5); display:flex; align-items:center; gap:20px; }
.rm-hero__glow{ position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(ellipse at 80% 25%, rgba(255,255,255,.16) 0%, transparent 55%); }
.rm-hero__logo{ position:relative; flex:0 0 auto; display:flex; align-items:center; pointer-events:none; }
.rm-hero__logo svg{ display:block; height:82px; width:auto; }
.rm-hero__body{ position:relative; flex:1; min-width:0; }
.rm-hero__title{ margin:0; font-size:23px; font-weight:800; color:#fff; letter-spacing:-.01em; }
.rm-hero__sub{ margin:5px 0 0; font-size:13px; color:rgba(255,255,255,.8); }
.rm-hero__stat{ position:relative; display:flex; flex-direction:column; align-items:flex-end; padding-right:4px; }
.rm-hero__stat b{ font-size:28px; font-weight:800; color:#fff; line-height:1; }
.rm-hero__stat span{ display:inline-flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase;
  letter-spacing:.08em; color:rgba(255,255,255,.74); margin-top:6px; }
.rm-refresh{ position:relative; appearance:none; width:40px; height:40px; border-radius:11px; flex:0 0 auto;
  border:1px solid rgba(255,255,255,.26); background:rgba(255,255,255,.12); color:#fff; font-size:17px; cursor:pointer;
  transition:background .15s, transform .12s; display:flex; align-items:center; justify-content:center; }
.rm-refresh:hover{ background:rgba(255,255,255,.24); }
.rm-refresh:disabled{ opacity:.6; cursor:not-allowed; }

.rm-card{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:20px 22px; margin-bottom:16px;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.14); }
.rm-cardhd{ display:flex; align-items:center; gap:10px; margin:0 0 14px; flex-wrap:wrap; }
.rm-cardhd h2{ margin:0; font-size:15px; font-weight:800; color:#1e293b; letter-spacing:-.01em; }
.rm-cardhd .rm-sub{ font-size:12px; color:#94a3b8; }
.rm-grid{ display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; }
.rm-field{ display:flex; flex-direction:column; min-width:0; }
.rm-lbl{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; margin-bottom:6px; }
.rm-input{ width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0;
  font-size:13.5px; font-family:inherit; background:#fff; color:#0f172a; transition:border-color .15s, box-shadow .15s; }
.rm-input:hover{ border-color:#cbd5e1; }
.rm-input:focus{ outline:none; border-color:#0d9488; box-shadow:0 0 0 3px rgba(13,148,136,.15); }
textarea.rm-input{ resize:vertical; min-height:60px; line-height:1.5; font-family:ui-monospace, monospace; font-size:12.5px; }

.rm-cta{ appearance:none; border:none; cursor:pointer; color:#fff; font-weight:700; font-size:14px; padding:11px 24px;
  border-radius:12px; background:linear-gradient(135deg,#0f766e,#0d9488 55%,#0891b2);
  box-shadow:0 8px 20px -4px rgba(13,148,136,.42); transition:filter .15s, transform .12s;
  display:inline-flex; align-items:center; gap:8px; }
.rm-cta:hover{ filter:brightness(1.06); }
.rm-cta:active{ transform:translateY(1px); }
.rm-cta:disabled{ opacity:.6; cursor:not-allowed; filter:none; box-shadow:none; }
.rm-cta.ghost{ background:#f1f5f9; color:#0f766e; box-shadow:none; border:1px solid #e2e8f0; }
.rm-cta.ghost:hover{ background:#e6f5f3; }
.rm-cta.sm{ padding:7px 13px; font-size:12.5px; border-radius:9px; }
.rm-cta.danger{ background:linear-gradient(135deg,#b91c1c,#dc2626 60%,#ef4444); box-shadow:0 8px 20px -4px rgba(220,38,38,.42); }

.rm-tablecard{ background:#fff; border:1px solid #eef0f5; border-radius:16px; overflow:hidden;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.12); margin-bottom:16px; }
.rm-table{ width:100%; border-collapse:collapse; font-size:13px; }
.rm-table th{ text-align:left; padding:11px 14px; background:#f8fafc; color:#64748b; font-size:10.5px;
  font-weight:700; text-transform:uppercase; letter-spacing:.07em; border-bottom:1px solid #eef0f5; }
.rm-table td{ padding:11px 14px; border-bottom:1px solid #f4f6fa; color:#334155; vertical-align:middle; }
.rm-table tr:last-child td{ border-bottom:none; }
.rm-table tr.sel td{ background:#f0fdfa; }
.rm-table tr.off td{ opacity:.55; }

.rm-node{ display:inline-flex; align-items:center; gap:8px; }
.rm-node b{ font-weight:700; color:#0f172a; }
.rm-code{ font-family:ui-monospace, monospace; font-size:11.5px; color:#0f766e; background:#f0fdfa;
  border:1px solid #ccfbf1; border-radius:6px; padding:1px 6px; }

.rm-pill{ display:inline-flex; align-items:center; gap:5px; padding:3px 9px; border-radius:999px;
  font-size:11px; font-weight:700; letter-spacing:.02em; }
.rm-pill.on{ background:#ecfdf5; color:#047857; border:1px solid #a7f3d0; }
.rm-pill.off{ background:#f1f5f9; color:#64748b; border:1px solid #e2e8f0; }
.rm-pill.warn{ background:#fffbeb; color:#b45309; border:1px solid #fde68a; }
.rm-pill.info{ background:#eff6ff; color:#1d4ed8; border:1px solid #bfdbfe; }

.rm-note{ border-radius:12px; padding:12px 14px; font-size:12.5px; line-height:1.55; margin-top:12px; }
.rm-note.ok{ background:#ecfdf5; border:1px solid #a7f3d0; color:#065f46; }
.rm-note.warn{ background:#fffbeb; border:1px solid #fde68a; color:#92400e; }
.rm-err{ background:#fef2f2; border:1px solid #fecaca; color:#b91c1c; border-radius:12px; padding:12px 14px;
  font-size:13px; margin-bottom:14px; }
.rm-empty{ padding:26px; text-align:center; color:#94a3b8; font-size:13px; }
.rm-actions{ display:flex; gap:6px; flex-wrap:wrap; }
.rm-bar{ height:7px; border-radius:999px; background:#e2e8f0; overflow:hidden; margin-top:8px; }
.rm-bar>i{ display:block; height:100%; background:linear-gradient(90deg,#0d9488,#0891b2); }
`;

const dbId = (d) => d?.id ?? d?.Id ?? d?.dbInfoId ?? d?.DBInfoId;
const dbName = (d) => d?.dbName ?? d?.DbName ?? d?.name ?? d?.Name ?? "";
const asArray = (d, ...keys) => {
  if (Array.isArray(d)) return d;
  for (const k of keys) if (Array.isArray(d?.[k])) return d[k];
  return [];
};

const emptyRule = () => ({
  id: 0, name: "", description: "", enabled: true, priority: 0,
  sourceDBInfoId: "", matchType: "Keyword", pattern: "", targetField: "", recordCategoryId: "",
});

const emptySchedule = () => ({
  cutoffBasis: "Event", retentionValue: "", retentionUnit: "Years",
  dispositionAction: "Destroy", transferTargetLocationId: "", sourceDBInfoId: "",
});

export default function RecordsManagementPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [series, setSeries] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  // file plan composer
  const [newName, setNewName] = useState("");
  const [newParent, setNewParent] = useState("");
  const [newCode, setNewCode] = useState("");

  // schedule editor
  const [selectedId, setSelectedId] = useState(null);
  const [schedule, setSchedule] = useState(emptySchedule());
  const [scheduleResult, setScheduleResult] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);

  // auto-declare rules
  const [ruleForm, setRuleForm] = useState(emptyRule());
  const [savingRule, setSavingRule] = useState(false);
  const [testFields, setTestFields] = useState("DocType=Contract");
  const [testFileName, setTestFileName] = useState("");
  const [testResult, setTestResult] = useState(null);

  // per-document declaration
  const [docDbId, setDocDbId] = useState("");
  const [docArchiveNo, setDocArchiveNo] = useState("");
  const [docState, setDocState] = useState(null);
  const [docSeriesId, setDocSeriesId] = useState("");
  const [docCutoff, setDocCutoff] = useState("");
  const [docBusy, setDocBusy] = useState(false);

  // bulk jobs
  const [jobDbId, setJobDbId] = useState("");
  const [jobMode, setJobMode] = useState("RuleDeclare");
  const [jobSeriesId, setJobSeriesId] = useState("");
  const [jobCutoff, setJobCutoff] = useState("");
  const [jobOnlyUndeclared, setJobOnlyUndeclared] = useState(true);
  const [jobOnlyMissingCutoff, setJobOnlyMissingCutoff] = useState(true);
  const [jobCondition, setJobCondition] = useState("");
  const [job, setJob] = useState(null);

  const selected = useMemo(
    () => series.find((s) => String(s.id ?? s.Id) === String(selectedId)) || null,
    [series, selectedId]);

  const activeCount = useMemo(() => series.filter((s) => s.isActive ?? s.IsActive).length, [series]);

  const loadSeries = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const data = await getRecordCategories(token, { includeInactive });
      setSeries(asArray(data, "items", "Items"));
    } catch (e) {
      setError(e?.message || t("recordsManagement.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, includeInactive, t]);

  const loadRules = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getRecordSeriesRules(token, {});
      setRules(asArray(data, "items", "Items"));
    } catch { /* the rules panel simply stays empty */ }
  }, [token]);

  useEffect(() => { loadSeries(); }, [loadSeries]);
  useEffect(() => { loadRules(); }, [loadRules]);

  useEffect(() => {
    if (!token) return;
    getDatabases(token).then((d) => setDatabases(asArray(d, "databases", "Databases"))).catch(() => {});
    getStorageLocations(token).then((d) => setLocations(asArray(d, "locations", "Locations"))).catch(() => {});
  }, [token]);

  // Load the selected series' schedule into the editor.
  useEffect(() => {
    if (!selected) { setSchedule(emptySchedule()); setScheduleResult(null); return; }
    setSchedule({
      cutoffBasis: selected.cutoffBasis ?? selected.CutoffBasis ?? "Event",
      retentionValue: selected.retentionValue ?? selected.RetentionValue ?? "",
      retentionUnit: selected.retentionUnit ?? selected.RetentionUnit ?? "Years",
      dispositionAction: selected.dispositionAction ?? selected.DispositionAction ?? "Destroy",
      transferTargetLocationId: selected.transferTargetLocationId ?? selected.TransferTargetLocationId ?? "",
      sourceDBInfoId: selected.sourceDBInfoId ?? selected.SourceDBInfoId ?? "",
    });
    setScheduleResult(null);
  }, [selected]);

  const fail = (e, key) => setError(e?.message || t(key));

  // ── File plan ───────────────────────────────────────────────────────────────

  const addSeries = async () => {
    if (!newName.trim()) return;
    setError("");
    try {
      await createRecordCategory(token, {
        name: newName.trim(),
        parentId: newParent ? Number(newParent) : null,
        recordCode: newCode.trim() || null,
      });
      setNewName(""); setNewCode("");
      await loadSeries();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
  };

  const renameSeries = async (node) => {
    const name = window.prompt(t("recordsManagement.renamePrompt"), node.name ?? node.Name);
    if (!name || !name.trim()) return;
    setError("");
    try {
      await updateRecordCategory(token, node.id ?? node.Id, { name: name.trim() });
      await loadSeries();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
  };

  const reparentSeries = async (node) => {
    const target = window.prompt(t("recordsManagement.movePrompt"), "");
    if (target === null) return;
    setError("");
    try {
      await moveRecordCategory(token, node.id ?? node.Id, {
        newParentId: target.trim() ? Number(target.trim()) : null,
      });
      await loadSeries();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
  };

  const toggleSeriesActive = async (node) => {
    const id = node.id ?? node.Id;
    const isActive = node.isActive ?? node.IsActive;
    if (isActive && !window.confirm(t("recordsManagement.archiveConfirm"))) return;
    setError("");
    try {
      if (isActive) await archiveRecordCategory(token, id);
      else await restoreRecordCategory(token, id);
      await loadSeries();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
  };

  // ── Schedule ────────────────────────────────────────────────────────────────

  const saveSchedule = async () => {
    if (!selected) return;
    setSavingSchedule(true); setError(""); setScheduleResult(null);
    try {
      const res = await setRecordSchedule(token, selected.id ?? selected.Id, {
        cutoffBasis: schedule.cutoffBasis,
        retentionValue: schedule.retentionValue === "" ? null : Number(schedule.retentionValue),
        retentionUnit: schedule.retentionUnit,
        dispositionAction: schedule.dispositionAction,
        transferTargetLocationId: schedule.transferTargetLocationId ? Number(schedule.transferTargetLocationId) : null,
        sourceDBInfoId: schedule.sourceDBInfoId ? Number(schedule.sourceDBInfoId) : null,
      });
      setScheduleResult(res);
      await loadSeries();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
    finally { setSavingSchedule(false); }
  };

  // ── Auto-declare rules ──────────────────────────────────────────────────────

  const saveRule = async () => {
    if (!ruleForm.name.trim() || !ruleForm.recordCategoryId) return;
    setSavingRule(true); setError("");
    const body = {
      name: ruleForm.name.trim(),
      description: ruleForm.description || null,
      enabled: ruleForm.enabled,
      priority: Number(ruleForm.priority) || 0,
      sourceDBInfoId: ruleForm.sourceDBInfoId ? Number(ruleForm.sourceDBInfoId) : null,
      matchType: ruleForm.matchType,
      pattern: ruleForm.pattern || null,
      targetField: ruleForm.targetField || null,
      recordCategoryId: Number(ruleForm.recordCategoryId),
    };
    try {
      if (ruleForm.id) await updateRecordSeriesRule(token, ruleForm.id, body);
      else await createRecordSeriesRule(token, body);
      setRuleForm(emptyRule());
      await loadRules();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
    finally { setSavingRule(false); }
  };

  const removeRule = async (r) => {
    if (!window.confirm(t("recordsManagement.deleteRuleConfirm"))) return;
    setError("");
    try { await deleteRecordSeriesRule(token, r.id ?? r.Id); await loadRules(); }
    catch (e) { fail(e, "recordsManagement.deleteFailed"); }
  };

  const runTest = async () => {
    setError(""); setTestResult(null);
    const fields = {};
    testFields.split("\n").forEach((line) => {
      const i = line.indexOf("=");
      if (i > 0) fields[line.slice(0, i).trim()] = line.slice(i + 1).trim();
    });
    try {
      const res = await testRecordSeriesRule(token, { fields, fileName: testFileName || null });
      setTestResult(res);
    } catch (e) { fail(e, "recordsManagement.testFailed"); }
  };

  // ── Per-document declaration ────────────────────────────────────────────────

  const lookupDoc = async () => {
    if (!docDbId || !docArchiveNo.trim()) return;
    setDocBusy(true); setError(""); setDocState(null);
    try {
      const res = await getDocumentRecord(token, docDbId, docArchiveNo.trim());
      setDocState(res);
      setDocSeriesId(res?.recordCategoryId ?? res?.RecordCategoryId ?? "");
    } catch (e) { fail(e, "recordsManagement.docLookupFailed"); }
    finally { setDocBusy(false); }
  };

  const applyDeclare = async (undeclare) => {
    if (!docDbId || !docArchiveNo.trim()) return;
    if (undeclare && !window.confirm(t("recordsManagement.undeclareConfirm"))) return;
    setDocBusy(true); setError("");
    try {
      await declareDocument(token, {
        databaseId: Number(docDbId),
        archiveNo: docArchiveNo.trim(),
        recordCategoryId: undeclare ? null : Number(docSeriesId),
        cutoffDate: !undeclare && docCutoff ? docCutoff : null,
      });
      await lookupDoc();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
    finally { setDocBusy(false); }
  };

  const applyCutoff = async () => {
    if (!docDbId || !docArchiveNo.trim()) return;
    setDocBusy(true); setError("");
    try {
      await setDocumentCutoff(token, {
        databaseId: Number(docDbId),
        archiveNo: docArchiveNo.trim(),
        cutoffDate: docCutoff || null,
      });
      await lookupDoc();
    } catch (e) { fail(e, "recordsManagement.saveFailed"); }
    finally { setDocBusy(false); }
  };

  // ── Bulk jobs ───────────────────────────────────────────────────────────────

  const startJob = async () => {
    if (!jobDbId) return;
    setError("");
    try {
      const res = await triggerRecordsBackfill(token, {
        databaseId: Number(jobDbId),
        mode: jobMode,
        recordCategoryId: jobSeriesId ? Number(jobSeriesId) : null,
        cutoffDate: jobCutoff || null,
        onlyUndeclared: jobOnlyUndeclared,
        onlyMissingCutoff: jobOnlyMissingCutoff,
        conditionJson: jobCondition.trim() || null,
      });
      setJob(res);
    } catch (e) { fail(e, "recordsManagement.jobFailed"); }
  };

  const refreshJob = useCallback(async () => {
    if (!token || !jobDbId) return;
    try { setJob(await getRecordsBackfillStatus(token, jobDbId)); } catch { /* transient */ }
  }, [token, jobDbId]);

  // Poll only while a job is actually running.
  useEffect(() => {
    if (job?.state !== "Running") return undefined;
    const h = setInterval(refreshJob, 2000);
    return () => clearInterval(h);
  }, [job?.state, refreshJob]);

  const stopJob = async () => {
    if (!jobDbId) return;
    try { await cancelRecordsBackfill(token, jobDbId); await refreshJob(); }
    catch (e) { fail(e, "recordsManagement.jobFailed"); }
  };

  const seriesLabel = (s) => `${"— ".repeat(s.depth ?? s.Depth ?? 0)}${s.name ?? s.Name}`;
  const seriesOptions = series.filter((s) => s.isActive ?? s.IsActive);

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <style>{RM_STYLES}</style>

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <div className="rm-hero">
          <div className="rm-hero__glow" />
          <div className="rm-hero__logo"><FolderTreeArt /></div>
          <div className="rm-hero__body">
            <h1 className="rm-hero__title">{t("recordsManagement.title")}</h1>
            <p className="rm-hero__sub">{t("recordsManagement.subtitle")}</p>
          </div>
          <div className="rm-hero__stat">
            <b>{activeCount}</b>
            <span>{t("recordsManagement.activeSeries")}</span>
          </div>
          <button className="rm-refresh" onClick={loadSeries} disabled={loading} title={t("recordsManagement.refresh")}>
            {loading ? "⟳" : "↺"}
          </button>
        </div>

        {error && <div className="rm-err">{error}</div>}

        {/* ── File plan ── */}
        <div className="rm-card">
          <div className="rm-cardhd">
            <h2>{t("recordsManagement.filePlan")}</h2>
            <span className="rm-sub">{t("recordsManagement.filePlanHint")}</span>
            <label style={{ marginLeft: "auto", fontSize: 12.5, color: "#475569", display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              {t("recordsManagement.showArchived")}
            </label>
          </div>

          <div className="rm-grid">
            <div className="rm-field" style={{ flex: "2 1 220px" }}>
              <label className="rm-lbl">{t("recordsManagement.seriesName")}</label>
              <input className="rm-input" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder={t("recordsManagement.seriesNamePlaceholder")} />
            </div>
            <div className="rm-field" style={{ flex: "1 1 180px" }}>
              <label className="rm-lbl">{t("recordsManagement.parentSeries")}</label>
              <select className="rm-input" value={newParent} onChange={(e) => setNewParent(e.target.value)}>
                <option value="">{t("recordsManagement.rootLevel")}</option>
                {seriesOptions.map((s) => (
                  <option key={s.id ?? s.Id} value={s.id ?? s.Id}>{seriesLabel(s)}</option>
                ))}
              </select>
            </div>
            <div className="rm-field" style={{ flex: "1 1 130px" }}>
              <label className="rm-lbl">{t("recordsManagement.recordCode")}</label>
              <input className="rm-input" value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="FIN-01" />
            </div>
            <button className="rm-cta" onClick={addSeries} disabled={!newName.trim()}>
              {t("recordsManagement.addSeries")}
            </button>
          </div>
        </div>

        <div className="rm-tablecard">
          <table className="rm-table">
            <thead>
              <tr>
                <th>{t("recordsManagement.series")}</th>
                <th>{t("recordsManagement.schedule")}</th>
                <th>{t("recordsManagement.disposition")}</th>
                <th>{t("recordsManagement.status")}</th>
                <th style={{ width: 240 }}>{t("recordsManagement.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {series.length === 0 && (
                <tr><td colSpan={5} className="rm-empty">{t("recordsManagement.noSeries")}</td></tr>
              )}
              {series.map((s) => {
                const id = s.id ?? s.Id;
                const active = s.isActive ?? s.IsActive;
                const rv = s.retentionValue ?? s.RetentionValue;
                const ru = s.retentionUnit ?? s.RetentionUnit;
                const basis = s.cutoffBasis ?? s.CutoffBasis;
                const act = s.dispositionAction ?? s.DispositionAction;
                const ruleId = s.retentionRuleId ?? s.RetentionRuleId;
                return (
                  <tr key={id} className={`${String(selectedId) === String(id) ? "sel" : ""} ${active ? "" : "off"}`}>
                    <td>
                      <span className="rm-node" style={{ paddingLeft: (s.depth ?? s.Depth ?? 0) * 16 }}>
                        <b>{s.name ?? s.Name}</b>
                        {(s.recordCode ?? s.RecordCode) && <span className="rm-code">{s.recordCode ?? s.RecordCode}</span>}
                      </span>
                    </td>
                    <td>
                      {rv ? `${rv} ${t(`recordsManagement.unit${ru}`, { defaultValue: ru })}` : t("recordsManagement.permanent")}
                      <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
                        {t(`recordsManagement.basis${basis}`, { defaultValue: basis })}
                      </div>
                    </td>
                    <td>{t(`recordsManagement.action${act}`, { defaultValue: act })}</td>
                    <td>
                      {!active
                        ? <span className="rm-pill off">{t("recordsManagement.archived")}</span>
                        : ruleId
                          ? <span className="rm-pill on">{t("recordsManagement.enforced")}</span>
                          : <span className="rm-pill warn">{t("recordsManagement.notEnforced")}</span>}
                    </td>
                    <td>
                      <div className="rm-actions">
                        <button className="rm-cta ghost sm" onClick={() => setSelectedId(id)}>{t("recordsManagement.editSchedule")}</button>
                        <button className="rm-cta ghost sm" onClick={() => renameSeries(s)}>{t("recordsManagement.rename")}</button>
                        <button className="rm-cta ghost sm" onClick={() => reparentSeries(s)}>{t("recordsManagement.move")}</button>
                        <button className={`rm-cta sm ${active ? "danger" : "ghost"}`} onClick={() => toggleSeriesActive(s)}>
                          {active ? t("recordsManagement.archive") : t("recordsManagement.restore")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Retention schedule ── */}
        {selected && (
          <div className="rm-card">
            <div className="rm-cardhd">
              <h2>{t("recordsManagement.scheduleFor", { name: selected.path ?? selected.Path ?? selected.name ?? selected.Name })}</h2>
              <span className="rm-sub">{t("recordsManagement.scheduleHint")}</span>
              <button className="rm-cta ghost sm" style={{ marginLeft: "auto" }} onClick={() => setSelectedId(null)}>
                {t("recordsManagement.close")}
              </button>
            </div>

            <div className="rm-grid">
              <div className="rm-field" style={{ flex: "1 1 180px" }}>
                <label className="rm-lbl">{t("recordsManagement.cutoffBasis")}</label>
                <select className="rm-input" value={schedule.cutoffBasis}
                  onChange={(e) => setSchedule({ ...schedule, cutoffBasis: e.target.value })}>
                  <option value="Event">{t("recordsManagement.basisEvent")}</option>
                  <option value="DocumentDate">{t("recordsManagement.basisDocumentDate")}</option>
                  <option value="IngestDate">{t("recordsManagement.basisIngestDate")}</option>
                </select>
              </div>
              <div className="rm-field" style={{ flex: "0 1 110px" }}>
                <label className="rm-lbl">{t("recordsManagement.retention")}</label>
                <input className="rm-input" type="number" min="0" value={schedule.retentionValue}
                  onChange={(e) => setSchedule({ ...schedule, retentionValue: e.target.value })} placeholder="7" />
              </div>
              <div className="rm-field" style={{ flex: "0 1 130px" }}>
                <label className="rm-lbl">{t("recordsManagement.unit")}</label>
                <select className="rm-input" value={schedule.retentionUnit}
                  onChange={(e) => setSchedule({ ...schedule, retentionUnit: e.target.value })}>
                  <option value="Days">{t("recordsManagement.unitDays")}</option>
                  <option value="Months">{t("recordsManagement.unitMonths")}</option>
                  <option value="Years">{t("recordsManagement.unitYears")}</option>
                </select>
              </div>
              <div className="rm-field" style={{ flex: "1 1 160px" }}>
                <label className="rm-lbl">{t("recordsManagement.disposition")}</label>
                <select className="rm-input" value={schedule.dispositionAction}
                  onChange={(e) => setSchedule({ ...schedule, dispositionAction: e.target.value })}>
                  <option value="Destroy">{t("recordsManagement.actionDestroy")}</option>
                  <option value="Transfer">{t("recordsManagement.actionTransfer")}</option>
                  <option value="Review">{t("recordsManagement.actionReview")}</option>
                </select>
              </div>
              {schedule.dispositionAction === "Transfer" && (
                <div className="rm-field" style={{ flex: "1 1 200px" }}>
                  <label className="rm-lbl">{t("recordsManagement.transferTarget")}</label>
                  <select className="rm-input" value={schedule.transferTargetLocationId}
                    onChange={(e) => setSchedule({ ...schedule, transferTargetLocationId: e.target.value })}>
                    <option value="">{t("recordsManagement.selectTarget")}</option>
                    {locations.map((l) => (
                      <option key={l.id ?? l.Id} value={l.id ?? l.Id}>{l.name ?? l.Name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="rm-field" style={{ flex: "1 1 200px" }}>
                <label className="rm-lbl">{t("recordsManagement.scopeDatabase")}</label>
                <select className="rm-input" value={schedule.sourceDBInfoId}
                  onChange={(e) => setSchedule({ ...schedule, sourceDBInfoId: e.target.value })}>
                  <option value="">{t("recordsManagement.noDatabase")}</option>
                  {databases.map((d) => (
                    <option key={dbId(d)} value={dbId(d)}>{dbName(d)}</option>
                  ))}
                </select>
              </div>
              <button className="rm-cta" onClick={saveSchedule} disabled={savingSchedule}>
                {savingSchedule ? t("recordsManagement.saving") : t("recordsManagement.saveSchedule")}
              </button>
            </div>

            {/* The server tells us whether this schedule is actually enforced — never imply it is. */}
            {scheduleResult && (
              <div className={`rm-note ${scheduleResult.enforced ? "ok" : "warn"}`}>
                {scheduleResult.enforced
                  ? t("recordsManagement.enforcedNote")
                  : `${t("recordsManagement.notEnforcedNote")} ${scheduleResult.reason || ""}`}
              </div>
            )}
          </div>
        )}

        {/* ── Auto-declare rules ── */}
        <div className="rm-card">
          <div className="rm-cardhd">
            <h2>{ruleForm.id ? t("recordsManagement.editRule") : t("recordsManagement.newRule")}</h2>
            <span className="rm-sub">{t("recordsManagement.rulesHint")}</span>
          </div>
          <div className="rm-grid">
            <div className="rm-field" style={{ flex: "2 1 200px" }}>
              <label className="rm-lbl">{t("recordsManagement.ruleName")}</label>
              <input className="rm-input" value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} />
            </div>
            <div className="rm-field" style={{ flex: "1 1 150px" }}>
              <label className="rm-lbl">{t("recordsManagement.matchType")}</label>
              <select className="rm-input" value={ruleForm.matchType}
                onChange={(e) => setRuleForm({ ...ruleForm, matchType: e.target.value })}>
                <option value="Keyword">{t("recordsManagement.matchKeyword")}</option>
                <option value="Regex">{t("recordsManagement.matchRegex")}</option>
                <option value="Field">{t("recordsManagement.matchField")}</option>
                <option value="Luhn">{t("recordsManagement.matchLuhn")}</option>
              </select>
            </div>
            <div className="rm-field" style={{ flex: "1 1 150px" }}>
              <label className="rm-lbl">{t("recordsManagement.targetField")}</label>
              <input className="rm-input" value={ruleForm.targetField}
                onChange={(e) => setRuleForm({ ...ruleForm, targetField: e.target.value })} placeholder="*" />
            </div>
            <div className="rm-field" style={{ flex: "2 1 220px" }}>
              <label className="rm-lbl">{t("recordsManagement.pattern")}</label>
              <input className="rm-input" value={ruleForm.pattern}
                onChange={(e) => setRuleForm({ ...ruleForm, pattern: e.target.value })} />
            </div>
            <div className="rm-field" style={{ flex: "1 1 200px" }}>
              <label className="rm-lbl">{t("recordsManagement.targetSeries")}</label>
              <select className="rm-input" value={ruleForm.recordCategoryId}
                onChange={(e) => setRuleForm({ ...ruleForm, recordCategoryId: e.target.value })}>
                <option value="">{t("recordsManagement.selectSeries")}</option>
                {seriesOptions.map((s) => (
                  <option key={s.id ?? s.Id} value={s.id ?? s.Id}>{seriesLabel(s)}</option>
                ))}
              </select>
            </div>
            <div className="rm-field" style={{ flex: "0 1 100px" }}>
              <label className="rm-lbl">{t("recordsManagement.priority")}</label>
              <input className="rm-input" type="number" value={ruleForm.priority}
                onChange={(e) => setRuleForm({ ...ruleForm, priority: e.target.value })} />
            </div>
            <button className="rm-cta" onClick={saveRule} disabled={savingRule || !ruleForm.name.trim() || !ruleForm.recordCategoryId}>
              {savingRule ? t("recordsManagement.saving") : t("recordsManagement.saveRule")}
            </button>
            {ruleForm.id > 0 && (
              <button className="rm-cta ghost" onClick={() => setRuleForm(emptyRule())}>{t("recordsManagement.cancel")}</button>
            )}
          </div>

          <div className="rm-grid" style={{ marginTop: 16 }}>
            <div className="rm-field" style={{ flex: "2 1 260px" }}>
              <label className="rm-lbl">{t("recordsManagement.testFields")}</label>
              <textarea className="rm-input" value={testFields} onChange={(e) => setTestFields(e.target.value)} />
            </div>
            <div className="rm-field" style={{ flex: "1 1 180px" }}>
              <label className="rm-lbl">{t("recordsManagement.testFileName")}</label>
              <input className="rm-input" value={testFileName} onChange={(e) => setTestFileName(e.target.value)} />
            </div>
            <button className="rm-cta ghost" onClick={runTest}>{t("recordsManagement.runTest")}</button>
          </div>

          {testResult && (
            <div className={`rm-note ${testResult.matched ? "ok" : "warn"}`}>
              {testResult.matched
                ? t("recordsManagement.testMatched", {
                    rule: testResult.ruleName ?? "—",
                    series: testResult.seriesPath ?? testResult.recordCategoryId,
                  })
                : t("recordsManagement.testNoMatch")}
              {testResult.matched && (
                <div style={{ marginTop: 6 }}>
                  {testResult.cutoffDate
                    ? t("recordsManagement.testCutoff", { date: String(testResult.cutoffDate).slice(0, 10) })
                    : t("recordsManagement.testNoCutoff")}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="rm-tablecard">
          <table className="rm-table">
            <thead>
              <tr>
                <th>{t("recordsManagement.ruleName")}</th>
                <th>{t("recordsManagement.matchType")}</th>
                <th>{t("recordsManagement.pattern")}</th>
                <th>{t("recordsManagement.targetSeries")}</th>
                <th>{t("recordsManagement.priority")}</th>
                <th style={{ width: 160 }}>{t("recordsManagement.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr><td colSpan={6} className="rm-empty">{t("recordsManagement.noRules")}</td></tr>
              )}
              {rules.map((r) => {
                const rid = r.id ?? r.Id;
                const target = series.find((s) => String(s.id ?? s.Id) === String(r.recordCategoryId ?? r.RecordCategoryId));
                return (
                  <tr key={rid} className={(r.enabled ?? r.Enabled) ? "" : "off"}>
                    <td><b>{r.name ?? r.Name}</b></td>
                    <td>{t(`recordsManagement.match${r.matchType ?? r.MatchType}`, { defaultValue: r.matchType ?? r.MatchType })}</td>
                    <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{r.pattern ?? r.Pattern}</td>
                    <td>{target ? (target.path ?? target.Path ?? target.name ?? target.Name) : `#${r.recordCategoryId ?? r.RecordCategoryId}`}</td>
                    <td>{r.priority ?? r.Priority}</td>
                    <td>
                      <div className="rm-actions">
                        <button className="rm-cta ghost sm" onClick={() => setRuleForm({
                          id: rid, name: r.name ?? r.Name, description: r.description ?? r.Description ?? "",
                          enabled: r.enabled ?? r.Enabled, priority: r.priority ?? r.Priority ?? 0,
                          sourceDBInfoId: r.sourceDBInfoId ?? r.SourceDBInfoId ?? "",
                          matchType: r.matchType ?? r.MatchType, pattern: r.pattern ?? r.Pattern ?? "",
                          targetField: r.targetField ?? r.TargetField ?? "",
                          recordCategoryId: r.recordCategoryId ?? r.RecordCategoryId,
                        })}>{t("recordsManagement.edit")}</button>
                        <button className="rm-cta danger sm" onClick={() => removeRule(r)}>{t("recordsManagement.delete")}</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Per-document declaration ── */}
        <div className="rm-card">
          <div className="rm-cardhd">
            <h2>{t("recordsManagement.documentPanel")}</h2>
            <span className="rm-sub">{t("recordsManagement.documentHint")}</span>
          </div>
          <div className="rm-grid">
            <div className="rm-field" style={{ flex: "1 1 200px" }}>
              <label className="rm-lbl">{t("recordsManagement.database")}</label>
              <select className="rm-input" value={docDbId} onChange={(e) => setDocDbId(e.target.value)}>
                <option value="">{t("recordsManagement.selectDatabase")}</option>
                {databases.map((d) => <option key={dbId(d)} value={dbId(d)}>{dbName(d)}</option>)}
              </select>
            </div>
            <div className="rm-field" style={{ flex: "1 1 180px" }}>
              <label className="rm-lbl">{t("recordsManagement.archiveNo")}</label>
              <input className="rm-input" value={docArchiveNo} onChange={(e) => setDocArchiveNo(e.target.value)} />
            </div>
            <button className="rm-cta ghost" onClick={lookupDoc} disabled={docBusy || !docDbId || !docArchiveNo.trim()}>
              {t("recordsManagement.lookup")}
            </button>
          </div>

          {docState && (
            <>
              <div className="rm-note ok" style={{ marginBottom: 4 }}>
                {docState.isDeclared ?? docState.IsDeclared
                  ? t("recordsManagement.docDeclared", {
                      series: docState.seriesPath ?? docState.SeriesPath ?? `#${docState.recordCategoryId ?? docState.RecordCategoryId}`,
                    })
                  : t("recordsManagement.docNotDeclared")}
                {" "}
                {(docState.recordCutoffDate ?? docState.RecordCutoffDate)
                  ? t("recordsManagement.docCutoff", { date: String(docState.recordCutoffDate ?? docState.RecordCutoffDate).slice(0, 10) })
                  : t("recordsManagement.docNoCutoff")}
              </div>
              <div className="rm-grid" style={{ marginTop: 12 }}>
                <div className="rm-field" style={{ flex: "1 1 220px" }}>
                  <label className="rm-lbl">{t("recordsManagement.targetSeries")}</label>
                  <select className="rm-input" value={docSeriesId} onChange={(e) => setDocSeriesId(e.target.value)}>
                    <option value="">{t("recordsManagement.selectSeries")}</option>
                    {seriesOptions.map((s) => <option key={s.id ?? s.Id} value={s.id ?? s.Id}>{seriesLabel(s)}</option>)}
                  </select>
                </div>
                <div className="rm-field" style={{ flex: "1 1 170px" }}>
                  <label className="rm-lbl">{t("recordsManagement.cutoffDate")}</label>
                  <input className="rm-input" type="date" value={docCutoff} onChange={(e) => setDocCutoff(e.target.value)} />
                </div>
                <button className="rm-cta" onClick={() => applyDeclare(false)} disabled={docBusy || !docSeriesId}>
                  {t("recordsManagement.declare")}
                </button>
                <button className="rm-cta ghost" onClick={applyCutoff} disabled={docBusy || !(docState.isDeclared ?? docState.IsDeclared)}>
                  {t("recordsManagement.applyCutoff")}
                </button>
                <button className="rm-cta danger" onClick={() => applyDeclare(true)}
                  disabled={docBusy || !(docState.isDeclared ?? docState.IsDeclared)}>
                  {t("recordsManagement.undeclare")}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Bulk jobs ── */}
        <div className="rm-card">
          <div className="rm-cardhd">
            <h2>{t("recordsManagement.bulkJobs")}</h2>
            <span className="rm-sub">{t("recordsManagement.bulkHint")}</span>
          </div>
          <div className="rm-grid">
            <div className="rm-field" style={{ flex: "1 1 200px" }}>
              <label className="rm-lbl">{t("recordsManagement.database")}</label>
              <select className="rm-input" value={jobDbId} onChange={(e) => setJobDbId(e.target.value)}>
                <option value="">{t("recordsManagement.selectDatabase")}</option>
                {databases.map((d) => <option key={dbId(d)} value={dbId(d)}>{dbName(d)}</option>)}
              </select>
            </div>
            <div className="rm-field" style={{ flex: "1 1 180px" }}>
              <label className="rm-lbl">{t("recordsManagement.jobMode")}</label>
              <select className="rm-input" value={jobMode} onChange={(e) => setJobMode(e.target.value)}>
                <option value="RuleDeclare">{t("recordsManagement.modeRuleDeclare")}</option>
                <option value="BulkDeclare">{t("recordsManagement.modeBulkDeclare")}</option>
                <option value="Cutoff">{t("recordsManagement.modeCutoff")}</option>
              </select>
            </div>
            {jobMode !== "RuleDeclare" && (
              <div className="rm-field" style={{ flex: "1 1 200px" }}>
                <label className="rm-lbl">{t("recordsManagement.targetSeries")}</label>
                <select className="rm-input" value={jobSeriesId} onChange={(e) => setJobSeriesId(e.target.value)}>
                  <option value="">{jobMode === "Cutoff" ? t("recordsManagement.allSeries") : t("recordsManagement.selectSeries")}</option>
                  {seriesOptions.map((s) => <option key={s.id ?? s.Id} value={s.id ?? s.Id}>{seriesLabel(s)}</option>)}
                </select>
              </div>
            )}
            {jobMode !== "RuleDeclare" && (
              <div className="rm-field" style={{ flex: "1 1 170px" }}>
                <label className="rm-lbl">{t("recordsManagement.cutoffDate")}</label>
                <input className="rm-input" type="date" value={jobCutoff} onChange={(e) => setJobCutoff(e.target.value)} />
              </div>
            )}
            <div className="rm-field" style={{ flex: "2 1 260px" }}>
              <label className="rm-lbl">{t("recordsManagement.condition")}</label>
              <input className="rm-input" value={jobCondition} onChange={(e) => setJobCondition(e.target.value)}
                placeholder='{"DocType":{"$eq":"Contract"}}' />
            </div>
            <label style={{ fontSize: 12.5, color: "#475569", display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox"
                checked={jobMode === "Cutoff" ? jobOnlyMissingCutoff : jobOnlyUndeclared}
                onChange={(e) => (jobMode === "Cutoff"
                  ? setJobOnlyMissingCutoff(e.target.checked)
                  : setJobOnlyUndeclared(e.target.checked))} />
              {jobMode === "Cutoff" ? t("recordsManagement.onlyMissingCutoff") : t("recordsManagement.onlyUndeclared")}
            </label>
            <button className="rm-cta" onClick={startJob} disabled={!jobDbId}>{t("recordsManagement.startJob")}</button>
            {job?.state === "Running" && (
              <button className="rm-cta danger" onClick={stopJob}>{t("recordsManagement.cancelJob")}</button>
            )}
          </div>

          {/* Re-cutting a running clock, or re-declaring filed records, changes retention on content
              already under a schedule — say so before it is started, not after. */}
          {((jobMode === "Cutoff" && !jobOnlyMissingCutoff) || (jobMode !== "Cutoff" && !jobOnlyUndeclared)) && (
            <div className="rm-note warn">{t("recordsManagement.overwriteWarning")}</div>
          )}

          {job && (
            <div className="rm-note ok" style={{ marginTop: 12 }}>
              <b>{t(`recordsManagement.jobState${job.state}`, { defaultValue: job.state })}</b>
              {" — "}
              {t("recordsManagement.jobProgress", { scanned: job.scanned ?? 0, affected: job.affected ?? 0 })}
              {job.error && <div style={{ color: "#b91c1c", marginTop: 6 }}>{job.error}</div>}
              <div className="rm-bar">
                <i style={{ width: job.state === "Completed" ? "100%" : (job.scanned ? "66%" : "8%") }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
